import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import type { ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';

import * as styles from './animated-pill-segment.css';

export interface AnimatedPillSegmentOption<K extends string> {
  key: K;
  label: ReactNode;
  /** The option cannot be chosen right now; `title` says why. */
  disabled?: boolean;
  title?: string;
  testId?: string;
}

export interface AnimatedPillSegmentProps<K extends string> {
  active: K;
  ariaLabel: string;
  disabled?: boolean;
  onChange: (key: K) => void;
  options: ReadonlyArray<AnimatedPillSegmentOption<K>>;
  /** Spreads the options evenly over the full width of the container. */
  fill?: boolean;
}

/** Controlled segmented navigation with one shared, centrally animated surface. */
export function AnimatedPillSegment<K extends string>({
  active,
  ariaLabel,
  disabled = false,
  onChange,
  options,
  fill = false,
}: AnimatedPillSegmentProps<K>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const optionRefs = useRef(new Map<K, HTMLButtonElement>());
  const previousRef = useRef<{ left: number; width: number } | null>(null);
  const animationRef = useRef<ReturnType<
    typeof streamLainAnimationController.play
  > | null>(null);

  const syncIndicator = useCallback(
    (animate: boolean) => {
      const indicator = indicatorRef.current;
      const target = optionRefs.current.get(active);
      if (!indicator || !target) return;

      const next = { left: target.offsetLeft, width: target.offsetWidth };
      if (next.width <= 0) {
        animationRef.current?.cancel();
        animationRef.current = null;
        indicator.style.opacity = '0';
        return;
      }

      const previous = previousRef.current;
      const geometryUnchanged =
        previous?.left === next.left && previous.width === next.width;
      // ResizeObserver can fire after the active option changed even when the
      // measured geometry did not. Keep the controller-owned motion alive in
      // that case instead of cancelling it on the next observer callback.
      if (!animate && geometryUnchanged) {
        indicator.style.opacity = '1';
        return;
      }

      animationRef.current?.cancel();
      animationRef.current = null;
      indicator.style.transform = `translateX(${next.left}px)`;
      indicator.style.width = `${next.width}px`;
      indicator.style.opacity = '1';
      previousRef.current = next;
      if (!animate || disabled || !previous || geometryUnchanged) {
        return;
      }

      animationRef.current = streamLainAnimationController.play(
        indicator,
        [
          {
            transform: `translateX(${previous.left}px)`,
            width: `${previous.width}px`,
          },
          {
            transform: `translateX(${next.left}px)`,
            width: `${next.width}px`,
          },
        ],
        'resize'
      );
    },
    [active, disabled]
  );

  useLayoutEffect(() => {
    syncIndicator(true);
  }, [options, syncIndicator]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => syncIndicator(false));
    observer.observe(root);
    optionRefs.current.forEach(option => observer.observe(option));
    return () => observer.disconnect();
  }, [options, syncIndicator]);

  useLayoutEffect(() => {
    const indicator = indicatorRef.current;
    return () => {
      animationRef.current?.cancel();
      if (indicator) streamLainAnimationController.cancel(indicator);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={styles.root}
      role="tablist"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      data-fill={fill || undefined}
    >
      <span
        ref={indicatorRef}
        className={styles.indicator}
        aria-hidden="true"
      />
      {options.map(option => (
        <button
          key={option.key}
          ref={element => {
            if (element) optionRefs.current.set(option.key, element);
            else optionRefs.current.delete(option.key);
          }}
          type="button"
          role="tab"
          aria-selected={option.key === active}
          aria-disabled={disabled || option.disabled || undefined}
          disabled={disabled || option.disabled}
          title={option.title}
          data-testid={option.testId}
          className={styles.option}
          onClick={() => {
            if (!disabled && !option.disabled) onChange(option.key);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
